import { Check, Plus, Search } from 'lucide-react';
import { Drawer } from 'vaul';
import type { ApiWatchlist, ChartSymbol } from '../types/api';
import { watchlistHasSymbol } from './AppShell.utils';
import type { WatchlistCategory } from './AppShell.types';

export function SymbolPicker({ open, symbols, watchlists, query, category, loading, onCategoryChange, onQueryChange, onOpenChange, onSelect, onAdd }: {
  open: boolean;
  symbols: ChartSymbol[];
  watchlists: ApiWatchlist[];
  query: string;
  category: WatchlistCategory;
  loading: boolean;
  onCategoryChange(category: WatchlistCategory): void;
  onQueryChange(query: string): void;
  onOpenChange(open: boolean): void;
  onSelect(symbol: ChartSymbol): void;
  onAdd(symbol: ChartSymbol): void;
}) {
  const normalizedQuery = query.trim().toLowerCase();
  const categories = [
    { value: 'all' as const, label: 'All' },
    { value: 'stock' as const, label: 'Stocks' },
    { value: 'crypto' as const, label: 'Crypto' },
    { value: 'forex' as const, label: 'Forex' },
  ];
  const filteredSymbols = symbols
    .filter((symbol) => category === 'all' || symbol.type === category)
    .filter((symbol) => !normalizedQuery
      || symbol.name.toLowerCase().includes(normalizedQuery)
      || symbol.description.toLowerCase().includes(normalizedQuery)
      || symbol.exchange.toLowerCase().includes(normalizedQuery))
    .slice(0, 60);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="drawer-overlay" />
        <Drawer.Content className="drawer-content symbol-picker" aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
          <div className="sheet-handle" aria-hidden="true" />
          <label className="symbol-search-box">
            <Search size={22} strokeWidth={1.8} />
            <input
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Search symbol, name, exchange"
            />
          </label>
          <div className="symbol-picker-categories" aria-label="Symbol categories">
            {categories.map((item) => (
              <button
                key={item.value}
                type="button"
                className={category === item.value ? 'symbol-picker-category active' : 'symbol-picker-category'}
                onClick={() => onCategoryChange(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="symbol-picker-list">
            {loading ? (
              <SymbolPickerSkeletonRows />
            ) : filteredSymbols.length > 0 ? filteredSymbols.map((symbol) => {
              const added = watchlists.length > 0 && watchlists.every((watchlist) => watchlistHasSymbol(watchlist, symbol));
              return (
                <div key={symbol.name} className="symbol-picker-row">
                  <button
                    type="button"
                    className="symbol-picker-row-main"
                    onClick={() => onSelect(symbol)}
                  >
                    <span className="symbol-main">
                      <strong>{symbol.name}</strong>
                      <span>{symbol.description}</span>
                    </span>
                    <span className="symbol-picker-meta">{symbol.exchange}</span>
                  </button>
                  <button
                    type="button"
                    className={added ? 'symbol-picker-add-btn symbol-picker-add-btn-added' : 'symbol-picker-add-btn'}
                    onClick={() => {
                      if (added) return;
                      onAdd(symbol);
                    }}
                    aria-label={added ? `${symbol.name} already added` : `Add ${symbol.name} to watchlist`}
                    disabled={added}
                  >
                    {added ? <Check size={18} strokeWidth={2.4} /> : <Plus size={18} strokeWidth={2.4} />}
                  </button>
                </div>
              );
            }) : (
              <div className="empty-state">No supported symbols found.</div>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function SymbolPickerSkeletonRows() {
  return Array.from({ length: 8 }, (_, index) => (
    <div key={index} className="symbol-picker-row symbol-picker-row-skeleton" aria-hidden="true">
      <div className="symbol-picker-row-main">
        <span className="symbol-main skeleton-main">
          <span className="skeleton-block skeleton-symbol" />
          <span className="skeleton-block skeleton-description" />
        </span>
        <span className="symbol-picker-meta symbol-picker-meta-skeleton">
          <span className="skeleton-block symbol-picker-exchange-skeleton" />
        </span>
      </div>
      <span className="symbol-picker-add-btn symbol-picker-add-btn-skeleton">
        <span className="skeleton-block symbol-picker-action-skeleton" />
      </span>
    </div>
  ));
}
